/**
 * Single source of truth for admin route access requirements.
 *
 * Both the router guards (`router.tsx` → `requireRouteAccess`) and the sidebar
 * nav gating (`AdminSidebar.tsx`) read from this one map, so the two can never
 * drift apart again — and neither can drift from the role/permission the
 * backend actually enforces for that surface.
 *
 * IMPORTANT: this is UX, not security. The server is the only real boundary
 * (every endpoint enforces its own role/permission — see src/server/routes).
 * This map exists so an authenticated user is cleanly redirected to
 * /admin/unauthorized instead of landing on a page that 403s on every call.
 *
 * Each entry carries EXACTLY ONE axis, matching what the backend enforces:
 *   - `platformAdmin`: Livepoint's platform admin grant (the cross-tenant
 *     surfaces; the backend's requirePlatformAdmin). Not a role: no tenant
 *     role, however high, reaches these.
 *   - `minimumRole`: a tenant role tier
 *   - `permission`:  a resource:action (the canonical RBAC unit for tenant surfaces)
 * A route with no entry inherits only the adminLayout `requireAuth` guard —
 * reachable by any authenticated user (e.g. the Dashboard).
 */

import type { AdminRole } from '../types/admin';

export interface RouteAccess {
  platformAdmin?: true;
  minimumRole?: AdminRole;
  permission?: { resource: string; action: string };
}

export const ADMIN_ROUTE_ACCESS: Record<string, RouteAccess> = {
  // Channels
  '/admin/agents': { permission: { resource: 'config', action: 'read' } },
  '/admin/voice-configs': { permission: { resource: 'config', action: 'read' } },
  '/admin/outbound-calls': { permission: { resource: 'config', action: 'read' } },
  '/admin/conversations': { permission: { resource: 'config', action: 'read' } },
  // The template list loads via GET /admin/templates (config:read); writes need
  // config:update, gated per-control in-page. Gate the ROUTE on config:read so a
  // viewer who can read templates isn't walled out (template:admin would be a
  // stricter FE gate than the server enforces).
  '/admin/email-templates': { permission: { resource: 'config', action: 'read' } },
  // Management
  '/admin/users': { permission: { resource: 'user', action: 'read' } },
  '/admin/api-keys': { permission: { resource: 'api_key', action: 'read' } },
  '/admin/integrations': { permission: { resource: 'integration', action: 'admin' } },
  // The current customer's own numbers (GET /admin/phone-numbers, config:read).
  // Editing (config:update) and history (audit_log:read) are gated in-page; a
  // platform admin reaches another customer's numbers by switching into it.
  '/admin/phone-numbers': { permission: { resource: 'config', action: 'read' } },
  // Monitoring
  // Audio monitoring is a global, cross-tenant view — every /admin/audio route
  // is platform-admin-only on the backend (a metrics:read viewer would
  // otherwise see it enabled then 403).
  '/admin/audio-monitor': { platformAdmin: true },
  '/admin/audit-logs': { permission: { resource: 'audit_log', action: 'read' } },
  // The cluster/System view exposes fleet topology + shared-infra internals — a
  // platform surface, platform-admin-only on the backend (GET /cluster), so a
  // metrics:read admin/operator/viewer doesn't see it enabled then 403.
  '/admin/system': { platformAdmin: true },
  // Development — platform-only. Evaluation data (A/B tests, feedback, failures)
  // lives in in-memory singletons with no tenant tag, so every /admin/evaluation
  // route is platform-admin-only on the backend (see src/server/routes/admin.ts).
  // NOT analytics:read — a tenant `admin` holds analytics:read and would
  // otherwise see it enabled, then hit a wall of 403s.
  '/admin/evaluation': { platformAdmin: true },
  // Livepoint's view of every tenant's usage and credit, and the credit tool.
  // Every /admin/platform route is platform-admin-only on the backend
  // (src/server/routes/platform.ts). The nav hides it from anyone else.
  // Tenant usage is a report (every tenant, and one tenant's cost breakdown);
  // Tenants lists each tenant's account, whose page holds the credit tool and
  // the tenant's billing settings, calling rules and API access.
  '/admin/platform/usage': { platformAdmin: true },
  '/admin/platform/usage/$tenantId': { platformAdmin: true },
  '/admin/platform/tenants': { platformAdmin: true },
  '/admin/platform/call-quality': { platformAdmin: true },
  '/admin/platform/tenants/$tenantId': { platformAdmin: true },
  // Every tenant's phone numbers (whether each is charged, its cost in £).
  '/admin/platform/numbers': { platformAdmin: true },
};
